import {
  faChevronDown,
  faChevronUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  Group,
  NumberInput,
  Popover,
  RangeSlider,
  Stack,
  Text,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { COLORS } from '../../utils/constants'

interface RangeSelectorProps {
  title: string
  min: number
  max: number
  value: [number | null, number | null]
  onChange: (value: [number | null, number | null]) => void
  opened: boolean
  onOpenedChange: (
    opened: boolean,
    newValue?: [number | null, number | null],
  ) => void
  summary: string
  isDefault: boolean
  onClear: () => void
  prefix?: string
  suffix?: string
  step?: number
  label?: (value: number) => string
}

const RangeSelector = ({
  title,
  min,
  max,
  value,
  onChange,
  opened,
  onOpenedChange,
  summary,
  isDefault,
  onClear,
  prefix = '',
  suffix = '',
  step = 1,
  label,
}: RangeSelectorProps) => {
  const [draft, setDraft] = useState<[number | null, number | null]>(value)

  // Initialize draft when popover opens
  useEffect(() => {
    if (opened) {
      setDraft(value)
    }
  }, [opened, value])

  const handleDone = () => {
    const hasChanged = draft[0] !== value[0] || draft[1] !== value[1]
    onOpenedChange(false, hasChanged ? draft : undefined)
    if (hasChanged) {
      onChange(draft)
    }
  }

  const getSliderValue = (
    input: [number | null, number | null],
    min: number,
    max: number,
  ) => [input[0] ?? min, input[1] ?? max]

  const minPlaceholder = draft[0] == null ? 'Min' : undefined
  const maxPlaceholder = draft[1] == null ? 'Max' : undefined

  return (
    <Popover
      opened={opened}
      onChange={(opened) => onOpenedChange(opened, undefined)}
      position='bottom'
      withArrow
      shadow='md'
      onClose={handleDone}
    >
      <Popover.Target>
        <Button
          variant='outline'
          radius='xl'
          px={20}
          style={{
            borderRadius: 30,
            marginRight: 8,
            paddingRight: 8,
            color: !isDefault ? 'white' : COLORS.LightGreen,
            borderColor: COLORS.LightGreen,
            background: !isDefault ? COLORS.LightGreen : 'white',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => onOpenedChange(!opened, undefined)}
        >
          {summary}
          {!isDefault ? (
            <FontAwesomeIcon
              icon={faTimes}
              style={{
                cursor: 'pointer',
                color: 'white',
                paddingLeft: 8,
                fontSize: 16,
              }}
              onClick={(e) => {
                e.stopPropagation()
                onClear()
              }}
            />
          ) : opened ? (
            <FontAwesomeIcon
              icon={faChevronUp}
              style={{
                paddingLeft: 8,
                fontSize: 16,
              }}
            />
          ) : (
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{
                paddingLeft: 8,
                fontSize: 16,
              }}
            />
          )}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap='sm'>
          <Box w={220}>
            <Text size='sm' fw={500} mb={5}>
              {title}
            </Text>
            <RangeSlider
              value={getSliderValue(draft, min, max) as [number, number]}
              onChange={(value: [number, number] | number[]) => {
                const tuple: [number, number] = [value[0], value[1]]
                const minVal = tuple[0] === min ? null : tuple[0]
                const maxVal = tuple[1] === max ? null : tuple[1]
                setDraft([minVal, maxVal])
              }}
              min={min}
              max={max}
              step={step}
              size='sm'
              label={label}
              color={COLORS.LightGreen}
            />
            <Group mt={8} gap={8} grow>
              <NumberInput
                min={min}
                max={draft[1] ?? max}
                value={draft[0] ?? ''}
                onChange={(val) => {
                  const v = typeof val === 'number' && !isNaN(val) ? val : null
                  setDraft([v, draft[1]])
                }}
                size='xs'
                hideControls
                styles={{ input: { textAlign: 'center' } }}
                aria-label='Min value'
                prefix={prefix}
                suffix={suffix}
                placeholder={minPlaceholder}
              />
              <NumberInput
                min={draft[0] ?? min}
                max={max}
                value={draft[1] ?? ''}
                onChange={(val) => {
                  const v = typeof val === 'number' && !isNaN(val) ? val : null
                  setDraft([draft[0], v])
                }}
                size='xs'
                hideControls
                styles={{ input: { textAlign: 'center' } }}
                aria-label='Max value'
                prefix={prefix}
                suffix={suffix}
                placeholder={maxPlaceholder}
              />
            </Group>
          </Box>
          <Button fullWidth color={COLORS.LightGreen} onClick={handleDone}>
            Done
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

export default RangeSelector
